import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AppConfigService, GridBaseQuery, GridHelperService } from '@core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { InvestigationFormResponseDto } from '../dtos/investigation-form-response.dto';
import { CreateInvestigationFormRequestDto } from '../dtos/create-investigation-form-request.dto';
import { CreateInvestigationFormResponseDto } from '../dtos/create-investigation-form-response.dto';
import { UpdateInvestigationFormRequestDto } from '../dtos/update-investigation-form-request.dto';
import { FormVersionsListResponseDto } from '../dtos/form-versions-list-response.dto';
import { FormVersionDetailResponseDto } from '../dtos/form-version-detail-response.dto';
import { AddFieldRequestDto } from '../dtos/add-field-request.dto';
import { UpdateFieldRequestDto } from '../dtos/update-field-request.dto';
import { ReorderFieldsRequestDto } from '../dtos/reorder-fields-request.dto';
import { FieldDefinitionLibraryResponseDto } from '../dtos/field-definition-library-response.dto';
import { FieldTypeResponseDto } from '../dtos/field-constraint-type-response.dto';
import { KendoDataSourceResultDto } from '@shared';

interface KendoDataSourceResult<T> {
  data: T[];
  total: number;
}

@Injectable({ providedIn: 'root' })
export class InvestigationFormApi {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(AppConfigService);
  private readonly _gridHelper = inject(GridHelperService);
  private readonly _baseUrl = `${this._config.versionedApiUrl}/investigations/forms`;

  public getAllInvestigationForms(): Observable<InvestigationFormResponseDto[]> {
    return this._http.get<KendoDataSourceResult<InvestigationFormResponseDto>>(this._baseUrl).pipe(
      map((result) => result.data)
    );
  }

  public getInvestigationFormsWithPaging(query: GridBaseQuery): Observable<KendoDataSourceResultDto<InvestigationFormResponseDto>> {
    const params = this._gridHelper.buildGridParams(query);
    return this._http.get<KendoDataSourceResult<InvestigationFormResponseDto>>(this._baseUrl, { params }).pipe(
      map((result) => ({
        data: result.data,
        totalElements: result.total,
        totalPages: 0,
      })),
    );
  }

  public getInvestigationFormsByInvestigationTypeId(investigationTypeId: string): Observable<InvestigationFormResponseDto[]> {
    const params = new HttpParams()
      .set('Filter', `((investigationTypeId~eq~${investigationTypeId}))`);

    return this._http.get<KendoDataSourceResult<InvestigationFormResponseDto>>(this._baseUrl, { params }).pipe(
      map((result) => result.data)
    );
  }

  public getInvestigationFormByInvestigationTypeId(investigationTypeId: string): Observable<InvestigationFormResponseDto | undefined> {
    return this._http.get<InvestigationFormResponseDto>(`${this._baseUrl}/by-investigation-type/${investigationTypeId}`).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) return of(undefined);
        throw error;
      })
    );
  }

  public getInvestigationFormById(id: string): Observable<InvestigationFormResponseDto | undefined> {
    const params = new HttpParams()
      .set('Filter', `((id~eq~${id}))`);

    return this._http.get<KendoDataSourceResult<InvestigationFormResponseDto>>(this._baseUrl, { params }).pipe(
      map((result) => (result.data.length > 0 ? result.data[0] : undefined))
    );
  }

  public createInvestigationForm(dto: CreateInvestigationFormRequestDto): Observable<CreateInvestigationFormResponseDto> {
    return this._http.post<CreateInvestigationFormResponseDto>(this._baseUrl, dto);
  }

  public updateInvestigationForm(id: string, dto: UpdateInvestigationFormRequestDto): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/${id}`, dto);
  }

  public deleteInvestigationForm(id: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/${id}`);
  }

  public getInvestigationFormVersionsByFormId(formId: string): Observable<FormVersionsListResponseDto> {
    return this._http.get<FormVersionsListResponseDto>(`${this._baseUrl}/${formId}/versions`);
  }

  public getInvestigationFormVersionDetailByVersionId(versionId: string): Observable<FormVersionDetailResponseDto> {
    return this._http.get<FormVersionDetailResponseDto>(`${this._baseUrl}/versions/${versionId}`);
  }

  public createInvestigationFormDraftVersion(formId: string): Observable<{ versionId: string }> {
    return this._http.post<{ versionId: string }>(`${this._baseUrl}/versions/draft`, { formId });
  }

  public publishDraft(formId: string): Observable<{ versionId: string }> {
    return this._http.post<{ versionId: string }>(`${this._baseUrl}/versions/publish`, { formId });
  }

  public discardDraft(formId: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/versions/${formId}/draft`);
  }

  public addField(versionId: string, dto: AddFieldRequestDto): Observable<{ id: string }> {
    return this._http.post<{ id: string }>(`${this._baseUrl}/versions/${versionId}/fields`, dto);
  }

  public updateField(versionId: string, fieldId: string, dto: UpdateFieldRequestDto): Observable<{ id: string }> {
    return this._http.put<{ id: string }>(`${this._baseUrl}/versions/${versionId}/fields/${fieldId}`, dto);
  }

  public removeField(versionId: string, fieldId: string): Observable<void> {
    return this._http.delete<void>(`${this._baseUrl}/versions/${versionId}/fields/${fieldId}`);
  }

  public reorderFields(versionId: string, dto: ReorderFieldsRequestDto): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/versions/${versionId}/fields/reorder`, dto);
  }

  public getFieldDefinitions(): Observable<FieldDefinitionLibraryResponseDto> {
    return this._http.get<FieldDefinitionLibraryResponseDto>(`${this._baseUrl}/field-definitions`);
  }

  public getFieldTypes(): Observable<FieldTypeResponseDto[]> {
    return this._http.get<FieldTypeResponseDto[]>(`${this._baseUrl}/field-types`);
  }
}
